<template>
  <div>
    <h3 class="vap-form__section-title">AWS Authentication</h3>
    <div class="form-row">
      <VasionInput
        id="rekognition-name"
        v-model="formFields.name"
        title="Amazon Rekognition Name"
        name="rekognition-name"
        placeholder="Amazon Rekognition Name..."
        inputType="top-white"
        required
        :autoFocus="true"
        :trimValue="true"
        @input="emitWrapperIsDirty"
      />
    </div>
    <div class="form-row">
      <div class="auth-method-radio">
        <label class="radio-label">
          <input
            type="radio"
            v-model="authMethod"
            value="keySecret"
            @change="onAuthMethodChanged"
          />
          <span>Key and Secret</span>
        </label>
        <label class="radio-label">
          <input
            type="radio"
            v-model="authMethod"
            value="roleAssumption"
            @change="onAuthMethodChanged"
          />
          <span>Role Assumption</span>
        </label>
      </div>
    </div>
    <div class="form-row" v-if="authMethod === 'keySecret'">
      <VasionInput
        id="aws-access-key-id"
        v-model="formFields.awsAccessKeyID"
        title="AWS Access Key ID"
        name="aws-access-key-id"
        placeholder="AWS Access Key ID..."
        inputType="top-white"
        required
        @input="emitWrapperIsDirty"
      />
      <VasionInput
        id="aws-secret-key"
        v-model="formFields.awsSecretKey"
        title="AWS Secret Key"
        name="aws-secret-key"
        type="password"
        placeholder="AWS Secret Key..."
        inputType="top-white"
        required
        preventPasswordAutofill
        preventNewPassword
        @input="emitWrapperIsDirty"
      />
      <VasionButton
        :classProp="'primary'"
        :isDisabled="!isNameAndAwsConnectionPresent"
        class="btn-margin-fix"
        @vasionButtonClicked="validateAwsCredentials()"
      >
        Validate
      </VasionButton>
    </div>
    <div class="form-row" v-if="authMethod === 'roleAssumption'">
      <VasionInput
        id="aws-role-arn"
        v-model="formFields.awsRoleArn"
        title="AWS Role ARN"
        name="aws-role-arn"
        placeholder="arn:aws:iam::123456789012:role/RoleName..."
        inputType="top-white"
        required
        @input="emitWrapperIsDirty"
      />
      <VasionInput
        id="aws-role-duration"
        v-model="formFields.awsRoleDuration"
        title="Duration (seconds)"
        name="aws-role-duration"
        placeholder="3600 to 43200 (1-12 hours)..."
        inputType="top-white"
        specialType="number"
        :minValue="3600"
        :maxValue="43200"
        required
        @input="emitWrapperIsDirty"
      />
      <VasionInput
        id="aws-role-external-id"
        v-model="formFields.awsRoleExternalId"
        title="External ID"
        name="aws-role-external-id"
        placeholder="External ID..."
        inputType="top-white"
        required
        :isDisabled="true"
        :copyToClipboard="true"
      />
      <VasionButton
        :classProp="'primary'"
        :isDisabled="!isNameAndRoleArnPresent"
        class="btn-margin-fix"
        @vasionButtonClicked="validateAwsCredentials()"
      >
        Validate
      </VasionButton>
    </div>
    <h3 class="vap-form__section-title">AWS Configuration</h3>
    <div class="form-row">
      <VasionDropList
        v-slot="slotItem"
        v-model="formFields.awsRegion"
        title="AWS Region"
        :dataArray="lists.awsRegions"
        width="100%"
        :showSearchField="true"
        type="plain-list"
        displayName="displayName"
        valueName="value"
        :required="true"
        :isDisabled="!isAwsAuthValid"
        :showIfEmpty="true"
        @input="
          emitWrapperIsDirty()
          getAwsBuckets()
        "
      >
        {{ slotItem.item.displayName }}
      </VasionDropList>
      <VasionDropList
        v-slot="slotItem"
        v-model="formFields.awsBucket"
        title="AWS Bucket"
        :dataArray="lists.awsBuckets"
        width="100%"
        :showSearchField="true"
        type="plain-list"
        displayName="displayName"
        valueName="value"
        :required="true"
        :showIfEmpty="true"
        :isDisabled="!(isAwsRegionPresent && !isAwsBucketListEmpty)"
        @input="emitWrapperIsDirty"
      >
        {{ slotItem.item.displayName }}
      </VasionDropList>
      <VasionButton
        :classProp="'primary'"
        :isDisabled="!isAwsRegionAndBucketSelected"
        class="btn-margin-fix"
        @vasionButtonClicked="getAwsCollection"
      >
        Validate
      </VasionButton>
    </div>
    <div class="form-row">
      <VasionDropList
        v-slot="slotItem"
        v-model="formFields.awsCollection"
        title="AWS Collection"
        :dataArray="lists.awsCollections"
        width="100%"
        :showSearchField="true"
        type="add"
        displayName="displayName"
        valueName="value"
        :required="true"
        :isDisabled="!isAwsCollectionChecked"
        :showIfEmpty="true"
        :forceOpen="forceOpenCollectionDroplist"
        @addItemClicked="showAddCollection = true"
        @input="emitWrapperIsDirty()"
      >
        {{ slotItem.item.displayName }}
      </VasionDropList>
      <VasionDropList
        v-slot="slotItem"
        v-model="formFields.endpoint"
        title="Process Type"
        :dataArray="lists.endpoints"
        width="100%"
        :showSearchField="true"
        type="plain-list"
        displayName="name"
        valueName="value"
        :required="true"
        :showIfEmpty="true"
        :isDisabled="!isCollectionSelected"
        @input="emitWrapperIsDirty"
      >
        {{ slotItem.item.name }}
      </VasionDropList>
    </div>
    <h3 class="vap-form__section-title">Process Configuration</h3>
    <div v-if="isEndpointSelected">
      <div class="form-row">
        <VasionDropList
          v-slot="slotItem"
          v-model="formFields.object"
          title="Object"
          :dataArray="lists.objects"
          width="100%"
          :showSearchField="true"
          type="plain-list"
          displayName="name"
          valueName="value"
          :required="true"
          :showIfEmpty="true"
          :isDisabled="!isCollectionSelected"
          @input="
            emitWrapperIsDirty()
            onObjectChanged()
          "
        >
          {{ slotItem.item.name }}
        </VasionDropList>
        <VasionDropList
          v-slot="slotItem"
          v-model="formFields.fieldId"
          title="Linking Field for User ID"
          width="100%"
          type="plain-list"
          displayName="title"
          valueName="id"
          :dataArray="objectFields"
          :showSearchField="false"
          :required="true"
          :showIfEmpty="true"
          :openAbove="true"
          :isDisabled="!isObjectAndEndpointSelected"
          @input="emitWrapperIsDirty"
        >
          {{ slotItem.item.title }}
        </VasionDropList>
        <VasionInput
          v-if="showSimilarityThreshold"
          id="rekognition-name"
          v-model="formFields.similarityThreshold"
          title="Minimum Similarity Threshold (%)"
          name="similarity-threshold"
          placeholder="0 - 100%..."
          inputType="top-white"
          specialType="number"
          :minValue="0"
          :maxValue="100"
          required
          @input="emitWrapperIsDirty"
        />
      </div>
    </div>

    <AddCollectionModal
      :awsId="formFields.awsAccessKeyID"
      :awsSecret="formFields.awsSecretKey"
      :awsRoleArn="formFields.awsRoleArn"
      :awsRoleDuration="formFields.awsRoleDuration"
      :awsRoleExternalId="formFields.awsRoleExternalId"
      :authMethod="authMethod"
      :id="dataToEdit.Id"
      :region="formFields.awsRegion.value"
      :show="showAddCollection"
      @close="showAddCollection = false"
      @collectionAdded="collectionAdded"
    />

    <VasionSnackbar
      id="snackbar-rekognition-form"
      ref="rekognitionSnackbar"
      :showSnackbarBool.sync="snackbar.showSnackbar"
      :snackbarImage="snackbar.snackbarImage"
      :snackbarSubTitle="snackbar.snackbarSubTitle"
      :snackbarTitle="snackbar.snackbarTitle"
    />
  </div>
</template>

<script>
import lists from './awsFieldsList.json'
import AddCollectionModal from '@/components/capture/AddCollectionModal.vue'

export default {
  name: 'RekognitionForm',
  components: {
    AddCollectionModal,
  },
  props: {
    rekognitionDataToEdit: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      authMethod: 'keySecret',
      awsAIPRekognitionType: 2,
      dataToEdit: this.rekognitionDataToEdit,
      endpoints: {
        HEADSHOT: 1,
        NEW_PHOTOS: 2,
      },
      forceOpenCollectionDroplist: false,
      formFields: {
        awsAccessKeyID: '',
        awsBucket: '',
        awsCollection: '',
        awsRegion: '',
        awsRoleArn: '',
        awsRoleDuration: 3600,
        awsRoleExternalId: '',
        awsSecretKey: '',
        endpoint: '',
        fieldId: null,
        name: '',
        object: '',
        similarityThreshold: 0,
      },
      isAwsAuthValid: false,
      isInitializingEdit: false,
      listedCollections: false,
      lists: {
        awsBuckets: [],
        awsCollections: [],
        awsRegions: [],
        endpoints: lists.rekognitionEndpointList,
        objects: [],
      },
      showAddCollection: false,
      snackbar: {
        showSnackbar: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
      },
    }
  },
  computed: {
    isAwsCollectionChecked() {
      return this.listedCollections
    },
    isAwsBucketListEmpty() {
      return this.lists.awsBuckets.length === 0
    },
    isAwsRegionAndBucketSelected() {
      return Boolean(
        this.formFields.awsRegion?.value && this.formFields.awsBucket?.value
      )
    },
    isAwsRegionPresent() {
      return Boolean(this.formFields.awsRegion?.value)
    },
    isCollectionSelected() {
      return Boolean(this.formFields.awsCollection?.value)
    },
    isEdit() {
      return this.dataToEdit && 'Id' in this.dataToEdit
    },
    isEndpointSelected() {
      return Boolean(this.formFields.endpoint?.value)
    },
    isFieldIdSelected() {
      return Boolean(this.formFields.fieldId?.id)
    },
    isTheFormValid() {
      let formValid = this.isNameAndAwsConnectionPresent
      formValid = formValid && this.isAwsRegionAndBucketSelected
      formValid =
        formValid &&
        this.isCollectionSelected &&
        this.isObjectAndEndpointSelected
      formValid = formValid && this.isEndpointSelected
      formValid = formValid && this.isFieldIdSelected

      if (this.formFields.endpoint && this.formFields.endpoint?.value === this.endpoints.NEW_PHOTOS) {
        let similarityThresholdValid = false
        const similarityThreshold = parseFloat(
          this.formFields.similarityThreshold
        )
        if (
          !isNaN(similarityThreshold) ||
          similarityThreshold < 0 ||
          similarityThreshold > 100
        ) {
          similarityThresholdValid = true
        }

        formValid = formValid && similarityThresholdValid
      }

      return formValid
    },
    isNameAndAwsConnectionPresent() {
      if (this.authMethod === 'keySecret') {
        return Boolean(
          this.formFields.name &&
            this.formFields.awsAccessKeyID &&
            this.formFields.awsSecretKey
        )
      } else {
        return Boolean(
          this.formFields.name &&
            this.formFields.awsRoleArn &&
            this.formFields.awsRoleExternalId
        )
      }
    },
    isNameAndRoleArnPresent() {
      return Boolean(
        this.formFields.name &&
          this.formFields.awsRoleArn &&
          this.formFields.awsRoleExternalId
      )
    },
    isObjectSelected() {
      return Boolean(this.formFields.object?.value)
    },
    isObjectAndEndpointSelected() {
      return Boolean(
        this.formFields.object?.value && this.formFields.endpoint?.value
      )
    },
    objectFields() {
      let objectFields = []
      const fieldNamesToIgnore = [
        'Workflow_Status',
        'Workflow_Approver',
        'Workflow_Due_Date',
        'Workflow_Initiator',
        'CreatedBy',
      ]
      const fields =
        this.$store.state.attributeForm.activeIndexForm.fields || []

      fields
        .filter(field => !fieldNamesToIgnore.includes(field.name))
        .map(field => {
          objectFields.push({
            id: field.fieldID,
            name: field.name,
            title: field.title,
          })
        })

      return objectFields
    },
    showSimilarityThreshold() {
      return this.formFields.endpoint?.value === this.endpoints.NEW_PHOTOS
    },
  },
  watch: {
    rekognitionDataToEdit(newValue) {
      this.dataToEdit = newValue
      if (
        this.isEdit &&
        Object.keys(this.dataToEdit).length &&
        !this.isInitializingEdit
      )
        this.setFormValues(this.dataToEdit)
    },
  },
  async created() {
    this.$emit('markLoadingStatus', true)
    this.lists.awsRegions = await this.getAwsRegions()
    this.lists.objects = await this.$store.dispatch('attributeForm/getForms')
    if (
      this.isEdit &&
      Object.keys(this.dataToEdit).length &&
      !this.isInitializingEdit
    )
      await this.setFormValues(this.dataToEdit)
    this.$emit('markLoadingStatus', false)
  },
  methods: {
    async collectionAdded(collectionName){
      await this.getAwsCollection()
      this.formFields.awsCollection = await this.lists.awsCollections.find(
        item => item.value === collectionName
      )
      this.forceOpenCollectionDroplist = true
      this.$nextTick(() => {
        this.forceOpenCollectionDroplist = false
      })
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async ensureAwsRoleExternalId() {
      if (this.authMethod !== 'roleAssumption' || this.formFields.awsRoleExternalId) return
      try {
        const response = await this.$store.dispatch('admin/getAwsRoleExternalId')
        const value = response?.data?.Value
        if (value != null && value !== '') {
          this.formFields.awsRoleExternalId = value
        } else {
          window.alert('Unable to retrieve AWS External ID from the server. Please try again or contact your administrator.')
        }
      } catch (error) {
        // Log the error for debugging purposes and notify the user
        // eslint-disable-next-line no-console
        console.error('Failed to retrieve AWS External ID:', error)
        window.alert('An error occurred while retrieving the AWS External ID. Please try again or contact your administrator.')
      }
    },
    async onAuthMethodChanged() {
      // Reset auth validation when switching methods
      this.isAwsAuthValid = false
      // Clear the fields for the method that's not selected
      if (this.authMethod === 'keySecret') {
        this.formFields.awsRoleArn = ''
        this.formFields.awsRoleDuration = 3600
        this.formFields.awsRoleExternalId = ''
      } else {
        this.formFields.awsAccessKeyID = ''
        this.formFields.awsSecretKey = ''
        await this.ensureAwsRoleExternalId()
      }
      this.emitWrapperIsDirty()
    },
    emitWrapperIsDirty() {
      const formValid = this.isTheFormValid

      this.$emit('markAsDirty')
      this.$emit('markFormStatus', formValid)
    },
    async getAwsBuckets(calledFromRegionSelection = true) {
      if (!this.formFields.awsRegion?.value) {
        this.formFields.awsBucket = []
        this.lists.awsBuckets = []
        return
      }

      if (calledFromRegionSelection) this.$emit('markLoadingStatus', true)

      const payload = {
        ...(this.isEdit && { id: this.dataToEdit.Id }),
        awsRegion: this.formFields.awsRegion.value,
        amazonType: this.awsAIPRekognitionType,
      }
      
      // Add auth-specific fields based on authMethod
      if (this.authMethod === 'keySecret') {
        payload.awsAccessKeyID = this.formFields.awsAccessKeyID
        payload.awsSecretKey = this.formFields.awsSecretKey
      } else {
        payload.awsRoleArn = this.formFields.awsRoleArn
        payload.awsRoleDuration = this.formFields.awsRoleDuration
        payload.awsRoleExternalId = this.formFields.awsRoleExternalId
      }

      const response = await this.$store.dispatch(
        'admin/listTextractS3Buckets',
        payload
      )
      let awsBuckets = response?.buckets || []

      awsBuckets = awsBuckets.map(awsBucket => ({
        value: awsBucket,
        displayName: awsBucket,
      }))
      this.lists.awsBuckets = awsBuckets

      this.formFields.awsBucket = []

      if (awsBuckets.length === 0) {
        const title = 'AWS Region'
        const subTitle = 'There are no buckets associated with this region'
        this.showSnackbar(title, subTitle, false)
      } else {
        this.$refs.rekognitionSnackbar.closeSnackbar()
      }

      if (calledFromRegionSelection) this.$emit('markLoadingStatus', false)
    },
    async getAwsCollection(calledFromRegionSelection = true) {
      // Check if required auth fields are present based on auth method
      const hasRequiredAuth = this.authMethod === 'keySecret'
        ? (this.formFields.awsAccessKeyID && this.formFields.awsSecretKey)
        : this.formFields.awsRoleArn
      
      if (!hasRequiredAuth || !this.formFields.awsRegion) {
        this.lists.awsCollections = []
        return
      }

      if (calledFromRegionSelection) this.$emit('markLoadingStatus', true)
      const payload = {
        ...(this.isEdit && { id: this.dataToEdit.Id }),
        awsRegion: this.formFields.awsRegion?.value,
      }
      
      // Add auth-specific fields based on authMethod
      if (this.authMethod === 'keySecret') {
        payload.awsAccessKeyID = this.formFields.awsAccessKeyID
        payload.awsSecretKey = this.formFields.awsSecretKey
      } else {
        payload.awsRoleArn = this.formFields.awsRoleArn
        payload.awsRoleDuration = this.formFields.awsRoleDuration
        payload.awsRoleExternalId = this.formFields.awsRoleExternalId
      }
      const response = await this.$store.dispatch(
        'admin/listRekognitionCollections',
        payload
      )

      const isValid = Boolean(response && response?.Values)
      const title = 'AWS Rekognition and Collections'
      const subTitle = isValid
        ? 'AWS Rekognition and Collection successfully validated'
        : response?.ResultMessage ||
          'Invalid AWS configuration. Please contact your administrator.'

      if (calledFromRegionSelection) this.showSnackbar(title, subTitle, isValid)

      let awsCollections = response?.Values || []
      awsCollections = awsCollections.map(awsCollection => ({
        value: awsCollection,
        displayName: awsCollection,
      }))
      this.lists.awsCollections = awsCollections

      if (awsCollections.length === 0) {
        const title = 'AWS Collections'
        const subTitle =
          'There are no Collections associated with this AWS account'
        this.showSnackbar(title, subTitle, false)
      }
      this.listedCollections = true

      if (calledFromRegionSelection) this.$emit('markLoadingStatus', false)
    },
    async getAwsRegions() {
      const response = await this.$store.dispatch('admin/getAwsRegions')
      let regions = []
      if (response && response?.data.Values) {
        regions = await response.data.Values.map(region => ({
          value: region,
          displayName: region,
        }))
      }
      return regions
    },
    async getObjectFields(objectId) {
      await this.$store.dispatch('attributeForm/getFormDetails', objectId)
    },
    onObjectChanged() {
      this.formFields.fieldId = null
      this.emitWrapperIsDirty()
      this.getObjectFields(this.formFields.object.value)
    },
    async setFormValues(data) {
      const validData = this.validateDataToEdit(data)
      if (!validData) {
        return
      }
      this.isInitializingEdit = true
      // AWS Authentication - detect auth method
      if (data.AwsConfig.RoleArn) {
        this.authMethod = 'roleAssumption'
        this.formFields.awsRoleArn = data.AwsConfig.RoleArn
        this.formFields.awsRoleDuration = data.AwsConfig.DurationSeconds || 3600
        this.formFields.awsRoleExternalId = data.AwsConfig.ExternalId || ''
        await this.ensureAwsRoleExternalId()
      } else {
        this.authMethod = 'keySecret'
        this.formFields.awsAccessKeyID = data.AwsConfig.ClientID
        this.formFields.awsSecretKey = data.AwsConfig.ClientSecret
      }
      this.formFields.name = data.Name
      this.isAwsAuthValid = true
      // AWS Configuration
      const awsRegionsLoaded = await this.waitForArrayValues(
        () => this.lists.awsRegions
      )
      if (!awsRegionsLoaded && this.lists.awsRegions.length === 0) {
        this.$emit('markLoadingStatus', false)
        const title = 'AWS Rekonition'
        const subTitle = 'Failed to load AWS regions. Please try again later.'
        this.showSnackbar(title, subTitle, false)
        return
      }
      this.formFields.awsRegion = await this.lists.awsRegions.find(
        item => item.value === data.AwsConfig.Region
      )

      await this.getAwsBuckets(false)
      this.formFields.awsBucket = await this.lists.awsBuckets.find(
        item => item.value === data.AwsConfig.BucketName
      )

      await this.getAwsCollection(false)
      this.formFields.awsCollection = await this.lists.awsCollections.find(
        item => item.value === data.RekognitionCollection
      )

      this.formFields.object = await this.lists.objects.find(
        item => item.value === data.IndexFormId
      )

      this.formFields.endpoint = await this.lists.endpoints.find(
        item => item.value === data.RekognitionEndpoint
      )

      await this.getObjectFields(this.formFields.object.value)
      this.formFields.fieldId = await this.objectFields.find(
        item => item.id === data.FieldID
      )

      this.formFields.similarityThreshold = data.MinimumSimilarityThreshold

      this.isInitializingEdit = false
    },
    showSnackbar(title, subTitle, image) {
      this.snackbar = {
        snackbarTitle: title,
        snackbarSubTitle: subTitle,
        snackbarImage: image,
        showSnackbar: true,
      }
    },
    async validateAwsCredentials() {
      this.$emit('markLoadingStatus', true)
      const payload = {
        ...(this.isEdit && { id: this.dataToEdit.Id }),
        amazonType: this.awsAIPRekognitionType,
      }
      
      // Add auth-specific fields based on authMethod
      if (this.authMethod === 'keySecret') {
        payload.awsAccessKeyID = this.formFields.awsAccessKeyID
        payload.awsSecretKey = this.formFields.awsSecretKey
      } else {
        payload.awsRoleArn = this.formFields.awsRoleArn
        payload.awsRoleDuration = this.formFields.awsRoleDuration
        payload.awsRoleExternalId = this.formFields.awsRoleExternalId
      }
      
      const response = await this.$store.dispatch(
        'admin/validateTextractConnection',
        payload
      )
      this.$emit('markLoadingStatus', false)

      const isValid = response && response.Value === 'True'
      this.isAwsAuthValid = isValid
      const title = 'AWS Connection'
      const subTitle = isValid
        ? 'AWS account successfully validated'
        : response?.ResultMessage ||
          'Invalid AWS account. Please contact your administrator.'
      this.showSnackbar(title, subTitle, isValid)
    },
    async validateDataToEdit(data) {
      if (!data || typeof data !== 'object') {
        return false
      }
      
      // Check if AwsConfig exists
      if (!data.AwsConfig || typeof data.AwsConfig !== 'object') {
        return false
      }
      
      // Common required fields
      const commonRequiredFields = [
        'Name',
        'AwsConfig.Region',
        'AwsConfig.BucketName',
        'RekognitionCollection',
        'IndexFormId',
        'RekognitionEndpoint',
        'FieldID',
        'MinimumSimilarityThreshold',
      ]
      
      // Check common fields
      for (const field of commonRequiredFields) {
        const keys = field.split('.')
        let value = data
        for (const key of keys) {
          if (!Object.prototype.hasOwnProperty.call(value, key)) {
            return false
          }
          value = value[key]
        }
      }
      
      // Check auth-specific fields - must have either RoleArn OR (ClientID AND ClientSecret)
      const hasRoleArn = data.AwsConfig.RoleArn
      const hasKeySecret = data.AwsConfig.ClientID && data.AwsConfig.ClientSecret
      
      if (!hasRoleArn && !hasKeySecret) {
        return false
      }

      return true
    },
    async waitForArrayValues(getArray, timeout = 5000, interval = 50) {
      const start = Date.now()
      while (getArray().length === 0) {
        if (Date.now() - start > timeout) {
          return false
        }
        await this.delay(interval)
      }
      return true
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.vap-form__section-title {
  font-size: 18px;
  margin-bottom: 0px;
  padding-left: 6px;
}
div.form-row {
  padding: 6px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  grid-gap: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
}
::v-deep .btn-margin-fix button {
  margin-left: 0px;
}
@media (min-width: $breakpoint-xl) {
  .btn-margin-fix {
    position: relative;
    top: 17px;
  }
}

.auth-method-radio {
  display: flex;
  gap: 24px;
  align-items: center;
  padding: 10px 0;
  
  .radio-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    
    input[type="radio"] {
      cursor: pointer;
      width: 16px;
      height: 16px;
    }
    
    span {
      user-select: none;
    }
  }
}
</style>
